import { TranslateService } from '@ngx-translate/core';
import { DialogMessageComponent } from 'app/dialog/dialog-message/dialog-message.component';
import { DialogService } from 'app/service/dialog.service';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UserService } from 'app/service/user.service';
import { UserRoute } from 'app/model/entity/user-route';
import { AreaTimeTable, Timetable, TimeTableDisplayRoute } from 'app/model/entity/timetable';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Helper } from 'app/common/helper';
import { TimetableService } from 'app/service/timetable.service';
import { TimetableRouteMapping } from 'app/model/entity/timetable-route';

class DropdownOption {
  id: Number;
  stopCode: string;
  label: string;
  isDisable: boolean;
}

class DisplayOption {
  displayLabel: String;
  optionLabels: Array<string>;
  selectedRoutes: Array<string>;
  selectedStops: Array<string>;
  selectedUserRoutes: Array<UserRoute>;
  updatedDatas: Array<AreaTimeTable>;
  selectStopDropdown: DropdownOption[];
  selectRouteDropdown: DropdownOption[][];
}

class RouteDataBase {
  obj: TimetableRouteMapping;
  routeIdMap: Map<String, String>;
}

@Component({
  selector: 'dialog-adjust-distance',
  templateUrl: './dialog-adjust-distance.component.html',
  styleUrls: ['./dialog-adjust-distance.component.scss']
})
export class DialogAdjustDistanceComponent implements OnInit {
  /**
   * list of user routes
   */
  userRoutes: Array<UserRoute>;

  /**
   * list of user routes
   */
  userRoutesMap: Map<Number, UserRoute>;

  mapStopCodeName: any;

  displayOptions: Array<DisplayOption>;

  constructor(
    private dialogRef: MatDialogRef<DialogAdjustDistanceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private userService: UserService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private timetableService: TimetableService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getAllRoutes();
  }

  /**
   * get all routes
   */
  private getAllRoutes(): void {
    this.userService.getAllRoutes().subscribe(
      (routesData: UserRoute[]) => {
        const routeMap = new Map<Number, UserRoute>();
        routesData.forEach(route => {
          routeMap.set(route.id, route);
        });
        this.userRoutes = routesData;
        this.userRoutesMap = routeMap;
        const display1TimeTableDisplayRoute = [];
        const display2TimeTableDisplayRoute = [];
        this.data.timetableSelected.routeDisplay.forEach(timeTableRoute => {
          if (timeTableRoute.display1) {
            display1TimeTableDisplayRoute.push(timeTableRoute.display1);
          }
          if (timeTableRoute.display2) {
            display2TimeTableDisplayRoute.push(timeTableRoute.display2);
          }
        });

        this.populateRouteData(display1TimeTableDisplayRoute, this.data.isDisplay2);
        this.updateAllPulldown(0);
        if (this.data.isDisplay2) {
          this.populateRouteData(display2TimeTableDisplayRoute, this.data.isDisplay2);
          this.updateAllPulldown(1);
        }
        this.cdr.detectChanges();
      },
      () => this.handleError()
    );
  }

  private populateRouteData = (timeTableDisplayRoutes: TimeTableDisplayRoute[], isMultidisplay: Boolean) => {
    const optionLabels = [];
    const selectedRoutes = [];
    const selectedStops = [];
    const selectedUserRoutes = [];
    const updatedDatas: AreaTimeTable[] = [];
    timeTableDisplayRoutes.forEach(timeTableDisplayRoute => {
      const userRoute = timeTableDisplayRoute.templateUserRoute.userRoute;
      optionLabels.push(timeTableDisplayRoute.templateUserRoute.displayName);
      selectedRoutes.push(this.buildDisplayRouteName(userRoute));
      selectedStops.push(userRoute ? userRoute.stopCode : '');
      selectedUserRoutes.push(userRoute ? this.userRoutesMap.get(userRoute.id) : userRoute);
      updatedDatas.push({
        timetableId: userRoute ? String(userRoute.id) : '',
        timetableIndex: timeTableDisplayRoute.templateUserRoute.timetableId
      });
    });
    if (!this.displayOptions) {
      this.displayOptions = [];
    }
    const displayLabel = isMultidisplay
      ? `${this.translateService.instant('menu-timetable-editor.display.name')}${this.displayOptions.length + 1}`
      : '';
    this.displayOptions.push({
      displayLabel: displayLabel,
      optionLabels: optionLabels,
      selectedRoutes,
      selectedStops,
      selectedUserRoutes,
      updatedDatas,
      selectStopDropdown: [],
      selectRouteDropdown: new Array(timeTableDisplayRoutes.length).fill([])
    });
  };

  private buildDisplayRouteName = (userRoute: UserRoute) => {
    if (!userRoute) {
      return '';
    }
    return `${userRoute.routeCode}_${userRoute.routeName}`;
  };

  private buildDisplayStopName = (userRoute: UserRoute) => {
    if (!userRoute) {
      return '';
    }
    return `${userRoute.stopCode}_${userRoute.stopName}`;
  };

  public updateAllPulldown(displayOptionIndex: number) {
    const display: DisplayOption = this.displayOptions[displayOptionIndex];
    const stopRouteMap = {};
    const mapStopCodeNameList = {};
    this.userRoutes.forEach(route => {
      if (!stopRouteMap[route.stopCode]) {
        stopRouteMap[route.stopCode] = { routeMap: {}, stopCode: route.stopCode, displayName: this.buildDisplayStopName(route) };
      }
      stopRouteMap[route.stopCode].routeMap[route.routeCode] = {
        selected: false,
        id: route.id,
        displayName: this.buildDisplayRouteName(route)
      };
      if (!mapStopCodeNameList[route.stopCode]) {
        mapStopCodeNameList[route.stopCode] = [];
      }
      if (!mapStopCodeNameList[route.stopCode].includes(route.stopName)) {
        mapStopCodeNameList[route.stopCode].push(route.stopName);
      }
    });

    display.selectedUserRoutes.forEach(selectedUserRoute => {
      if (
        selectedUserRoute &&
        stopRouteMap[selectedUserRoute.stopCode] &&
        stopRouteMap[selectedUserRoute.stopCode].routeMap[selectedUserRoute.routeCode]
      ) {
        stopRouteMap[selectedUserRoute.stopCode].routeMap[selectedUserRoute.routeCode].selected = true;
      }
    });

    const stopOptions = [];
    const mapStopCodeName = {};
    Object.values(stopRouteMap).forEach(stopRoute => {
      const listStopName = mapStopCodeNameList[stopRoute['stopCode']];
      if (listStopName.length > 1) {
        stopRoute['displayName'] = `${stopRoute['stopCode']}_${listStopName.join('_')}`;
      }
      mapStopCodeName[stopRoute['stopCode']] = stopRoute['displayName'];
      const dropdownOption = new DropdownOption();
      dropdownOption.label = stopRoute['displayName'];
      dropdownOption.stopCode = stopRoute['stopCode'];
      dropdownOption.isDisable = Object.values(stopRoute['routeMap']).every(select => select['selected']);
      stopOptions.push(dropdownOption);
    });

    this.mapStopCodeName = mapStopCodeName;

    this.displayOptions[displayOptionIndex].selectStopDropdown = stopOptions;

    let firstSelectedStop = '';
    let hasUnselectedRoute = false;
    for (let i = 0; i < display.selectedUserRoutes.length; i++) {
      const routeOptions = [];
      const selectedStop = this.displayOptions[displayOptionIndex].selectedStops[i];
      if (selectedStop) {
        const stopCode = selectedStop;
        firstSelectedStop = stopCode;
        if (stopRouteMap[stopCode].routeMap) {
          Object.values(stopRouteMap[stopCode].routeMap).forEach(route => {
            const dropdownOption = new DropdownOption();
            dropdownOption.id = route['id'];
            dropdownOption.label = route['displayName'];
            dropdownOption.isDisable = route['selected'];
            routeOptions.push(dropdownOption);
          });
        }
      } else {
        hasUnselectedRoute = true;
      }
      this.displayOptions[displayOptionIndex].selectRouteDropdown[i] = routeOptions;
    }

    if (firstSelectedStop && hasUnselectedRoute) {
      for (let i = 0; i < display.selectedUserRoutes.length; i++) {
        const routeOptions = [];
        const selectedStop = this.displayOptions[displayOptionIndex].selectedStops[i];
        if (!selectedStop) {
          if (stopRouteMap[firstSelectedStop].routeMap) {
            Object.values(stopRouteMap[firstSelectedStop].routeMap).forEach(route => {
              const dropdownOption = new DropdownOption();
              dropdownOption.id = route['id'];
              dropdownOption.label = route['displayName'];
              dropdownOption.isDisable = route['selected'];
              routeOptions.push(dropdownOption);
            });
          }
          this.displayOptions[displayOptionIndex].selectRouteDropdown[i] = routeOptions;
        }
      }
    }
  }

  public changeStop(env, displayOptionIndex: number): void {
    for (let optionIndex = 0; optionIndex < this.displayOptions[displayOptionIndex].selectedRoutes.length; optionIndex++) {
      this.displayOptions[displayOptionIndex].selectedUserRoutes[optionIndex] = null;
      this.displayOptions[displayOptionIndex].selectedStops[optionIndex] = env.target.value;
      this.displayOptions[displayOptionIndex].updatedDatas[optionIndex].timetableId = '';
      this.displayOptions[displayOptionIndex].selectedRoutes[optionIndex] = '';
    }

    this.updateAllPulldown(displayOptionIndex);
  }

  public changeRoute(env, displayOptionIndex: number, optionIndex: number): void {
    if (env.target.value) {
      const selectedUser = this.userRoutes.find(userRoute => String(userRoute.id) === env.target.value);
      this.displayOptions[displayOptionIndex].selectedUserRoutes[optionIndex] = selectedUser;
      this.displayOptions[displayOptionIndex].selectedRoutes[optionIndex] = this.buildDisplayRouteName(selectedUser);
      this.displayOptions[displayOptionIndex].updatedDatas[optionIndex].timetableId = env.target.value;
    } else {
      this.displayOptions[displayOptionIndex].selectedUserRoutes[optionIndex] = null;
      this.displayOptions[displayOptionIndex].selectedRoutes[optionIndex] = '';
      this.displayOptions[displayOptionIndex].updatedDatas[optionIndex].timetableId = env.target.value;
    }

    this.updateAllPulldown(displayOptionIndex);
  }

  public haveUnselected(): boolean {
    if (!this.displayOptions) {
      return true;
    }
    if (this.displayOptions[0].updatedDatas.find(data => !data.timetableId)) {
      return true;
    }
    if (this.data.isDisplay2 && this.displayOptions[1].updatedDatas.find(data => !data.timetableId)) {
      return true;
    }
    return false;
  }

  /**
   * save
   * @returns
   */
  public save(): void {
    if (this.haveUnselected()) {
      return;
    }
    this.displayOptions[0].updatedDatas.forEach(data => (data.display = 1));
    const areaTimeTables = this.displayOptions[0].updatedDatas;
    if (this.data.isDisplay2 && this.displayOptions[1]) {
      this.displayOptions[1].updatedDatas.forEach(data => (data.display = 2));
      areaTimeTables.push(...this.displayOptions[1].updatedDatas);
    }

    const routeMappingMap = new Map<string, RouteDataBase>();
    areaTimeTables.forEach(areaTimeTable => {
      const key = `${areaTimeTable.display}`;
      let mapping = routeMappingMap.get(key);
      if (!mapping) {
        mapping = new RouteDataBase();
        mapping.obj = new TimetableRouteMapping();
        mapping.obj.display = areaTimeTable.display;
        mapping.obj.timetableId = this.data.timetableSelected.id;

        mapping.routeIdMap = new Map<string, string>();
        routeMappingMap.set(key, mapping);
      }
      mapping.routeIdMap.set(areaTimeTable.timetableIndex, areaTimeTable.timetableId);
    });

    const routeDataBase = [...routeMappingMap.values()];
    const saveObjList: TimetableRouteMapping[] = [];
    routeDataBase.forEach(routeDB => {
      routeDB.obj.routeMapping = JSON.stringify(Array.from(routeDB.routeIdMap.entries()));
      saveObjList.push(routeDB.obj);
    });

    this.timetableService.saveTimetableRoutes(saveObjList).subscribe(
      () => {
        this.dialogRef.close({ isUpdated: true });
      },
      error => Helper.handleError(error, this.translateService, this.dialogService)
    );
  }

  private handleError(): void {
    this.dialogService.showDialog(DialogMessageComponent, {
      data: {
        title: this.translateService.instant('dialog-error.title'),
        text: this.translateService.instant('dialog-error.msg')
      }
    });
  }
}

export interface DialogData {
  timetableSelected: Timetable;
  isDisplay2: Boolean;
}
